import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  X, Plus, Minus, Search, User as UserIcon, UserX, Banknote, CreditCard,
  CircleDollarSign, Mail, MessageSquare, Loader2, CheckCircle2, ExternalLink,
} from 'lucide-react';
import { useBranding } from '../../context/BrandingContext';
import { staffApi } from '../../services/api';

/**
 * Cash-desk modal for selling event tickets in person.
 *
 * Flow:
 *  1. Operator picks tiers + quantities (limited by per-tier remaining).
 *  2. Optionally attaches a CRM customer (live search) for loyalty points,
 *     OR proceeds as walk-in (anonymous).
 *  3. Picks payment method: Contanti / POS / Altro.
 *  4. Picks delivery channels (Email / SMS).
 *  5. Confirms → receipt screen with summary + buttons.
 */
export default function StaffSellTicketsModal({ eventId, event, tiers, onClose, onSold }) {
  const { primary_color } = useBranding();
  const pc = primary_color || '#D44E20';

  // Cart
  const initialCart = useMemo(() => {
    const out = {};
    for (const t of tiers) out[t.name] = 0;
    return out;
  }, [tiers]);
  const [cart, setCart] = useState(initialCart);

  // Buyer
  const [mode, setMode] = useState('member'); // 'member' | 'anonymous'
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [linked, setLinked] = useState(null); // {id,name,phone,email,points,tier}
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');

  // Payment + delivery
  const [payment, setPayment] = useState('cash'); // cash | card_external | other
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(false);
  const [note, setNote] = useState('');

  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState(null);

  const suggestTimer = useRef(null);

  useEffect(() => {
    if (mode !== 'member') { setSuggestions([]); setShowSuggestions(false); return; }
    if (suggestTimer.current) clearTimeout(suggestTimer.current);
    if (query.length < 2) { setSuggestions([]); setShowSuggestions(false); return; }
    suggestTimer.current = setTimeout(async () => {
      try {
        const res = await staffApi.get(`/customers/suggest?q=${encodeURIComponent(query)}`);
        const list = res.data.customers || [];
        setSuggestions(list);
        setShowSuggestions(list.length > 0);
      } catch { setSuggestions([]); }
    }, 250);
    return () => clearTimeout(suggestTimer.current);
  }, [query, mode]);

  const tiersByName = useMemo(() => Object.fromEntries(tiers.map(t => [t.name, t])), [tiers]);

  const total = useMemo(() => {
    let s = 0;
    for (const [name, qty] of Object.entries(cart)) {
      const t = tiersByName[name];
      if (!t) continue;
      s += (t.price || 0) * (qty || 0);
    }
    return Math.round(s * 100) / 100;
  }, [cart, tiersByName]);

  const totalTickets = useMemo(
    () => Object.values(cart).reduce((a, b) => a + (b || 0), 0),
    [cart]
  );

  const inc = (name) => {
    setCart(prev => {
      const t = tiersByName[name];
      const cur = prev[name] || 0;
      const remaining = (t.capacity === null || t.capacity === undefined)
        ? Infinity
        : Math.max(0, t.capacity - t.sold);
      if (cur + 1 > remaining) {
        toast.error(`Solo ${remaining} biglietti disponibili per ${name}`);
        return prev;
      }
      return { ...prev, [name]: cur + 1 };
    });
  };
  const dec = (name) => {
    setCart(prev => ({ ...prev, [name]: Math.max(0, (prev[name] || 0) - 1) }));
  };

  const pickCustomer = (c) => {
    setLinked(c);
    setShowSuggestions(false);
    setQuery('');
    // Pre-fill contact + name fields from CRM
    const parts = (c.name || '').trim().split(/\s+/);
    setFirstName(parts[0] || '');
    setLastName(parts.slice(1).join(' '));
    setPhone(c.phone || '');
    setEmail(c.email || '');
  };

  const unlink = () => {
    setLinked(null);
    setFirstName('');
    setLastName('');
    setPhone('');
    setEmail('');
  };

  const validate = () => {
    if (totalTickets <= 0) { toast.error('Aggiungi almeno un biglietto'); return false; }
    if (!firstName.trim()) { toast.error('Inserisci il nome dell\'acquirente'); return false; }
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const lines = Object.entries(cart)
        .filter(([, q]) => q > 0)
        .map(([tier_name, quantity]) => ({ tier_name, quantity }));
      const body = {
        buyer_first_name: firstName.trim(),
        buyer_last_name: lastName.trim(),
        buyer_phone: phone.trim() || null,
        buyer_email: email.trim() || null,
        lines,
        payment_method: payment,
        operator_note: note.trim(),
        link_customer_id: mode === 'member' && linked ? linked.id : null,
        send_email: sendEmail && !!email.trim(),
        send_sms: sendSms && !!phone.trim(),
      };
      const res = await staffApi.post(`/staff/events/${eventId}/sell-tickets`, body);
      setReceipt(res.data);
      const channels = [];
      if (sendEmail && email.trim()) channels.push('Email');
      if (sendSms && phone.trim()) channels.push('SMS');
      toast.success(
        channels.length
          ? `Vendita completata. ${channels.join(' + ')} inviato.`
          : 'Vendita completata.'
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore durante la vendita');
    } finally {
      setSubmitting(false);
    }
  };

  const finish = () => {
    onSold?.();
  };

  /* ── Receipt screen ──────────────────────────────────────────────── */
  if (receipt) {
    return (
      <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4">
        <div className="bg-[#18181E] w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl border border-white/10 max-h-[92vh] overflow-y-auto" data-testid="staff-sell-receipt">
          <div className="p-6">
            <div className="flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: `${pc}25` }}>
                <CheckCircle2 size={28} style={{ color: pc }} />
              </div>
              <h2 className="text-white font-bold text-xl">Vendita completata</h2>
              <p className="text-[#888] text-sm mt-1">{event.name}</p>
              <p className="text-white font-bold text-3xl tabular-nums mt-4" data-testid="staff-sell-receipt-total">
                € {(receipt.total_eur || 0).toFixed(2)}
              </p>
              <p className="text-[#888] text-xs mt-0.5 uppercase tracking-wide">
                {receipt.tickets?.length || 0} biglietti · {labelForPayment(receipt.payment_method)}
              </p>
            </div>

            <div className="mt-6 space-y-2">
              {(receipt.tickets || []).map((t, i) => (
                <div key={t.id} data-testid={`staff-sell-receipt-ticket-${i}`} className="bg-[#0E0E12] border border-white/5 rounded-xl p-3 flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-white text-sm font-semibold truncate">{t.tier_name}</p>
                    <p className="text-[#666] text-[11px] font-mono truncate">{t.qr_code}</p>
                  </div>
                  <span className="text-[10px] uppercase tracking-wide text-[#888]">#{i + 1}</span>
                </div>
              ))}
            </div>

            {receipt.public_link && (
              <a
                href={receipt.public_link}
                target="_blank"
                rel="noreferrer"
                data-testid="staff-sell-receipt-open-link"
                className="mt-4 w-full h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center gap-2 text-white text-sm hover:bg-white/10"
              >
                <ExternalLink size={14} /> Apri pagina biglietti
              </a>
            )}

            <button
              onClick={finish}
              data-testid="staff-sell-receipt-done-btn"
              className="mt-3 w-full h-12 rounded-xl font-bold text-white text-base active:scale-[0.98] transition-all"
              style={{ backgroundColor: pc }}
            >
              Fatto
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Sale form ───────────────────────────────────────────────────── */
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="staff-sell-modal">
      <div className="bg-[#18181E] w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl border border-white/10 max-h-[92vh] overflow-y-auto pb-32 relative">
        {/* Sticky header */}
        <div className="sticky top-0 z-10 bg-[#18181E] border-b border-white/5 px-5 py-3 flex items-center justify-between">
          <div>
            <p className="text-[#888] text-[11px] uppercase tracking-wide">Vendita biglietti</p>
            <h3 className="text-white font-bold text-base truncate max-w-[240px]">{event.name}</h3>
          </div>
          <button
            onClick={onClose}
            data-testid="staff-sell-modal-close-btn"
            className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-[#888] hover:text-white"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-5">
          {/* Tier picker */}
          <section>
            <p className="text-[#888] text-xs uppercase tracking-wide mb-2">Fasce biglietti</p>
            <div className="space-y-2">
              {tiers.map((t) => {
                const uncapped = t.capacity === null || t.capacity === undefined;
                const remaining = uncapped ? null : Math.max(0, t.capacity - t.sold);
                const qty = cart[t.name] || 0;
                const disabled = !uncapped && remaining === 0;
                return (
                  <div
                    key={t.name}
                    data-testid={`staff-sell-tier-${t.name}`}
                    className={`bg-[#0E0E12] border rounded-xl p-3 flex items-center justify-between ${disabled ? 'opacity-50 border-white/5' : 'border-white/10'}`}
                  >
                    <div className="min-w-0 mr-2">
                      <p className="text-white text-sm font-semibold truncate">{t.name}</p>
                      <p className="text-[#888] text-xs tabular-nums">
                        € {t.price.toFixed(2)} · {uncapped ? 'illimitati' : `${remaining} disp.`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => dec(t.name)}
                        disabled={qty === 0}
                        data-testid={`staff-sell-dec-${t.name}`}
                        className="w-8 h-8 rounded-full bg-white/5 text-white disabled:opacity-30 flex items-center justify-center hover:bg-white/10"
                      >
                        <Minus size={14} />
                      </button>
                      <span className="text-white text-base font-bold tabular-nums w-6 text-center" data-testid={`staff-sell-qty-${t.name}`}>
                        {qty}
                      </span>
                      <button
                        onClick={() => inc(t.name)}
                        disabled={disabled}
                        data-testid={`staff-sell-inc-${t.name}`}
                        className="w-8 h-8 rounded-full text-white disabled:opacity-30 flex items-center justify-center"
                        style={{ backgroundColor: `${pc}33` }}
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Buyer */}
          <section>
            <p className="text-[#888] text-xs uppercase tracking-wide mb-2">Acquirente</p>
            <div className="flex gap-2 mb-2">
              <button
                onClick={() => { setMode('member'); }}
                data-testid="staff-sell-mode-member"
                className={`flex-1 h-10 rounded-xl text-sm font-medium border ${mode === 'member' ? 'text-white border-transparent' : 'text-[#888] border-white/10 bg-white/5'}`}
                style={mode === 'member' ? { backgroundColor: pc } : undefined}
              >
                <span className="inline-flex items-center gap-1.5"><UserIcon size={14} /> Cliente CRM</span>
              </button>
              <button
                onClick={() => { setMode('anonymous'); unlink(); }}
                data-testid="staff-sell-mode-anonymous"
                className={`flex-1 h-10 rounded-xl text-sm font-medium border ${mode === 'anonymous' ? 'text-white border-transparent' : 'text-[#888] border-white/10 bg-white/5'}`}
                style={mode === 'anonymous' ? { backgroundColor: pc } : undefined}
              >
                <span className="inline-flex items-center gap-1.5"><UserX size={14} /> Anonimo</span>
              </button>
            </div>

            {mode === 'member' && !linked && (
              <div className="relative">
                <input
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Cerca per nome o telefono…"
                  data-testid="staff-sell-customer-search"
                  className="w-full h-11 bg-[#0E0E12] text-white rounded-xl pl-10 pr-3 border border-white/10 focus:outline-none placeholder:text-[#444]"
                  autoComplete="off"
                />
                <Search size={14} className="absolute left-3.5 top-3.5 text-[#555]" />
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-[#1E1E26] border border-white/10 rounded-xl overflow-hidden z-20 shadow-2xl max-h-60 overflow-y-auto" data-testid="staff-sell-suggestions">
                    {suggestions.map(c => (
                      <button
                        key={c.id}
                        onClick={() => pickCustomer(c)}
                        data-testid={`staff-sell-suggest-${c.id}`}
                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/5 text-left border-b border-white/5 last:border-0"
                      >
                        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${pc}20` }}>
                          <UserIcon size={12} style={{ color: pc }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm truncate">{c.name}</p>
                          <p className="text-[#888] text-[11px]">{c.phone}</p>
                        </div>
                        <span className="text-xs font-bold tabular-nums" style={{ color: pc }}>{c.points} pt</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {mode === 'member' && linked && (
              <div className="bg-[#0E0E12] border border-white/10 rounded-xl p-3 flex items-center justify-between" data-testid="staff-sell-linked-customer">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${pc}25` }}>
                    <UserIcon size={14} style={{ color: pc }} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium truncate">{linked.name}</p>
                    <p className="text-[#888] text-[11px]">{linked.phone} · {linked.points} pt</p>
                  </div>
                </div>
                <button onClick={unlink} data-testid="staff-sell-unlink-btn" className="text-[#888] text-xs hover:text-white">Cambia</button>
              </div>
            )}

            {/* Contact / name fields */}
            <div className="grid grid-cols-2 gap-2 mt-2">
              <input
                placeholder="Nome*"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                data-testid="staff-sell-first-name"
                className="h-11 bg-[#0E0E12] text-white text-sm rounded-xl px-3 border border-white/10 placeholder:text-[#444] focus:outline-none"
              />
              <input
                placeholder="Cognome"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                data-testid="staff-sell-last-name"
                className="h-11 bg-[#0E0E12] text-white text-sm rounded-xl px-3 border border-white/10 placeholder:text-[#444] focus:outline-none"
              />
              <input
                placeholder="Telefono"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                inputMode="tel"
                data-testid="staff-sell-phone"
                className="h-11 bg-[#0E0E12] text-white text-sm rounded-xl px-3 border border-white/10 placeholder:text-[#444] focus:outline-none"
              />
              <input
                placeholder="Email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                inputMode="email"
                data-testid="staff-sell-email"
                className="h-11 bg-[#0E0E12] text-white text-sm rounded-xl px-3 border border-white/10 placeholder:text-[#444] focus:outline-none"
              />
            </div>
            {mode === 'member' && linked && (
              <p className="text-[10px] mt-1.5" style={{ color: pc }}>
                +{Math.floor(total)} punti loyalty accreditati a {linked.name?.split(' ')[0]}.
              </p>
            )}
          </section>

          {/* Payment */}
          <section>
            <p className="text-[#888] text-xs uppercase tracking-wide mb-2">Metodo di pagamento</p>
            <div className="grid grid-cols-3 gap-2">
              <PaymentBtn icon={Banknote} label="Contanti" active={payment === 'cash'} onClick={() => setPayment('cash')} pc={pc} testid="staff-sell-pay-cash" />
              <PaymentBtn icon={CreditCard} label="POS" active={payment === 'card_external'} onClick={() => setPayment('card_external')} pc={pc} testid="staff-sell-pay-card" />
              <PaymentBtn icon={CircleDollarSign} label="Altro" active={payment === 'other'} onClick={() => setPayment('other')} pc={pc} testid="staff-sell-pay-other" />
            </div>
          </section>

          {/* Delivery */}
          <section>
            <p className="text-[#888] text-xs uppercase tracking-wide mb-2">Invio biglietti</p>
            <div className="space-y-2">
              <Toggle icon={Mail} label="Invia per Email" checked={sendEmail} onChange={setSendEmail} pc={pc} testid="staff-sell-toggle-email" />
              <Toggle icon={MessageSquare} label="Invia per SMS" checked={sendSms} onChange={setSendSms} pc={pc} testid="staff-sell-toggle-sms" />
            </div>
          </section>

          {/* Note */}
          <section>
            <p className="text-[#888] text-xs uppercase tracking-wide mb-2">Nota operatore (opzionale)</p>
            <input
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="es. consegnato in contanti, sconto cortesia…"
              maxLength={300}
              data-testid="staff-sell-note"
              className="w-full h-11 bg-[#0E0E12] text-white text-sm rounded-xl px-3 border border-white/10 placeholder:text-[#444] focus:outline-none"
            />
          </section>
        </div>

        {/* Sticky footer */}
        <div className="absolute bottom-0 left-0 right-0 bg-[#18181E] border-t border-white/5 px-5 py-3 flex items-center gap-3">
          <div className="flex-1">
            <p className="text-[#888] text-[11px] uppercase tracking-wide">Totale</p>
            <p className="text-white font-bold text-2xl tabular-nums" data-testid="staff-sell-total">€ {total.toFixed(2)}</p>
            <p className="text-[#666] text-[10px]">{totalTickets} biglietti</p>
          </div>
          <button
            onClick={submit}
            disabled={submitting || totalTickets === 0}
            data-testid="staff-sell-confirm-btn"
            className="h-12 px-5 rounded-xl font-bold text-white text-sm flex items-center justify-center gap-2 disabled:opacity-40 active:scale-95 transition-all"
            style={{ backgroundColor: pc }}
          >
            {submitting ? <Loader2 className="animate-spin" size={16} /> : null}
            Conferma vendita
          </button>
        </div>
      </div>
    </div>
  );
}

function labelForPayment(m) {
  if (m === 'cash') return 'Contanti';
  if (m === 'card_external') return 'POS esterno';
  return 'Altro';
}

function PaymentBtn({ icon: Icon, label, active, onClick, pc, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`h-12 rounded-xl text-xs font-medium flex flex-col items-center justify-center gap-0.5 border ${active ? 'text-white border-transparent' : 'text-[#888] border-white/10 bg-white/5'}`}
      style={active ? { backgroundColor: pc } : undefined}
    >
      <Icon size={14} />
      {label}
    </button>
  );
}

function Toggle({ icon: Icon, label, checked, onChange, pc, testid }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      data-testid={testid}
      className="w-full bg-[#0E0E12] border border-white/10 rounded-xl px-3 h-11 flex items-center justify-between"
    >
      <span className="inline-flex items-center gap-2 text-white text-sm">
        <Icon size={14} className="text-[#888]" /> {label}
      </span>
      <span className={`w-9 h-5 rounded-full relative transition-colors ${checked ? '' : 'bg-white/10'}`} style={checked ? { backgroundColor: pc } : undefined}>
        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${checked ? 'right-0.5' : 'left-0.5'}`} />
      </span>
    </button>
  );
}
